import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X } from 'lucide-react';
import {
  RadarChart as RadarChartBase,
  PolarGrid as PolarGridBase,
  PolarAngleAxis as PolarAngleAxisBase,
  PolarRadiusAxis as PolarRadiusAxisBase,
  Radar as RadarBase,
  ResponsiveContainer as ResponsiveContainerBase,
  Legend as LegendBase,
} from 'recharts';
import apiClient from '../../api/client';
import { Molecule } from '../../types/chemistry';
import { getMoleculeName } from './MoleculeGrid';
import StructureImage from './StructureImage';

const RChart = RadarChartBase as any;
const PGrid = PolarGridBase as any;
const PAngle = PolarAngleAxisBase as any;
const PRadius = PolarRadiusAxisBase as any;
const RRadar = RadarBase as any;
const RContainer = ResponsiveContainerBase as any;
const RLegend = LegendBase as any;

interface Props {
  molecules: Molecule[];
  onClose: () => void;
}

interface PropsData {
  mw?: number; logp?: number; tpsa?: number; hbd?: number; hba?: number;
  rotatable_bonds?: number; ring_count?: number; fsp3?: number;
  aromatic_rings?: number; heavy_atoms?: number; formal_charge?: number;
  molar_refractivity?: number; formula?: string;
}

const COMPOUND_COLORS = ['#0D9488', '#f59e0b', '#8b5cf6', '#ec4899'];

const PROP_DEFS: { key: keyof PropsData; label: string; unit: string; good: [number, number]; best: 'low' | 'high' }[] = [
  { key: 'mw', label: 'MW', unit: 'Da', good: [150, 500], best: 'low' },
  { key: 'logp', label: 'LogP', unit: '', good: [-0.4, 5], best: 'low' },
  { key: 'tpsa', label: 'TPSA', unit: '\u00c5\u00b2', good: [20, 140], best: 'low' },
  { key: 'hbd', label: 'HBD', unit: '', good: [0, 5], best: 'low' },
  { key: 'hba', label: 'HBA', unit: '', good: [0, 10], best: 'low' },
  { key: 'rotatable_bonds', label: 'Rot. Bonds', unit: '', good: [0, 10], best: 'low' },
  { key: 'ring_count', label: 'Rings', unit: '', good: [1, 6], best: 'low' },
  { key: 'fsp3', label: 'Fsp3', unit: '', good: [0.25, 1], best: 'high' },
];

const RADAR_AXES = [
  { key: 'mw', label: 'MW', max: 800 },
  { key: 'logp', label: 'LogP', max: 8, offset: 2 },
  { key: 'tpsa', label: 'TPSA', max: 200 },
  { key: 'hbd', label: 'HBD', max: 8 },
  { key: 'hba', label: 'HBA', max: 15 },
  { key: 'rotatable_bonds', label: 'RotBonds', max: 15 },
];

const ragColor = (val: number, good: [number, number]) => {
  if (val >= good[0] && val <= good[1]) return '#16a34a';
  const diff = val < good[0] ? good[0] - val : val - good[1];
  const range = good[1] - good[0];
  return diff / range > 0.5 ? '#dc2626' : '#d97706';
};

const CompoundComparison: React.FC<Props> = ({ molecules, onClose }) => {
  const [svgs, setSvgs] = useState<Record<number, string>>({});
  const [propsData, setPropsData] = useState<Record<number, PropsData>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const fetchAll = async () => {
      const svgPromises = molecules.map(async (mol) => {
        try {
          const resp = await apiClient.post('/v2/indigo/render', {
            structure: { value: mol.smiles, type: 'RAW' },
            width: '200px', height: '140px',
          }, { responseType: 'text' });
          return { id: mol.id, svg: resp.data };
        } catch { return { id: mol.id, svg: '' }; }
      });

      const propPromises = molecules.map(async (mol) => {
        try {
          const resp = await apiClient.post('/v2/properties/calculate', { smiles: mol.smiles });
          return { id: mol.id, data: resp.data as PropsData };
        } catch { return { id: mol.id, data: {} as PropsData }; }
      });

      const [svgResults, propResults] = await Promise.all([
        Promise.all(svgPromises),
        Promise.all(propPromises),
      ]);

      if (cancelled) return;
      const svgMap: Record<number, string> = {};
      svgResults.forEach((r) => { svgMap[r.id] = r.svg; });
      setSvgs(svgMap);

      const propMap: Record<number, PropsData> = {};
      propResults.forEach((r) => { propMap[r.id] = r.data; });
      setPropsData(propMap);
      setLoading(false);
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [molecules]);

  const names = useMemo(() =>
    molecules.map((m) => getMoleculeName(m.properties) || m.smiles.slice(0, 25)),
    [molecules]
  );

  const radarData = useMemo(() => {
    return RADAR_AXES.map((axis) => {
      const row: any = { property: axis.label };
      molecules.forEach((mol, i) => {
        const p = propsData[mol.id];
        if (!p) { row[`c${i}`] = 0; return; }
        const raw = (p as any)[axis.key] ?? 0;
        const offset = (axis as any).offset ?? 0;
        row[`c${i}`] = Math.max(0, Math.min(100, ((raw + offset) / axis.max) * 100));
      });
      return row;
    });
  }, [molecules, propsData]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg)', border: '1px solid var(--card-border)',
          borderRadius: 16, width: '90vw', maxWidth: 1100, maxHeight: '85vh',
          overflow: 'auto', padding: 24,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Compare {molecules.length} Compounds
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Side-by-side property comparison with drug-likeness radar
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
            Loading structures and properties...
          </div>
        ) : (
          <>
            {/* Structure images */}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${molecules.length}, 1fr)`, gap: 12, marginBottom: 20 }}>
              {molecules.map((mol, i) => (
                <div key={mol.id} style={{
                  background: 'var(--hover-bg)', borderRadius: 10, padding: 12, textAlign: 'center',
                  border: `2px solid ${COMPOUND_COLORS[i]}22`,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COMPOUND_COLORS[i], marginBottom: 6 }}>
                    {names[i]}
                  </div>
                  {svgs[mol.id] ? (
                    <div dangerouslySetInnerHTML={{ __html: svgs[mol.id] }} style={{ display: 'flex', justifyContent: 'center' }} />
                  ) : (
                    <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 11 }}>
                      No structure
                    </div>
                  )}
                  <div style={{ marginTop: 4, display: 'flex', justifyContent: 'center' }}>
                    <StructureImage smiles={mol.smiles} size={80} lazy />
                  </div>
                </div>
              ))}
            </div>

            {/* Property comparison table */}
            <div style={{ marginBottom: 20, background: 'var(--hover-bg)', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-muted)' }}>Property</th>
                    {molecules.map((mol, i) => (
                      <th key={mol.id} style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: COMPOUND_COLORS[i] }}>
                        {names[i]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PROP_DEFS.map((prop) => {
                    const vals = molecules.map((m) => (propsData[m.id] as any)?.[prop.key]);
                    const numVals = vals.filter((v): v is number => v != null);
                    const bestVal = numVals.length > 0
                      ? (prop.best === 'low' ? Math.min(...numVals) : Math.max(...numVals))
                      : null;

                    return (
                      <tr key={prop.key} style={{ borderBottom: '1px solid var(--card-border)' }}>
                        <td style={{ padding: '6px 12px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                          {prop.label} {prop.unit && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>({prop.unit})</span>}
                        </td>
                        {vals.map((v, i) => {
                          if (v == null) return <td key={i} style={{ textAlign: 'right', padding: '6px 12px', color: 'var(--text-muted)' }}>—</td>;
                          const color = ragColor(v, prop.good);
                          const isBest = numVals.length > 1 && v === bestVal;
                          return (
                            <td key={i} style={{
                              textAlign: 'right', padding: '6px 12px', fontFamily: 'monospace',
                              color, fontWeight: isBest ? 700 : 400,
                              backgroundColor: isBest ? `${color}11` : undefined,
                            }}>
                              {typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : v}
                              {isBest && ' \u2605'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Radar chart */}
            <div style={{ background: 'var(--hover-bg)', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Drug-likeness Radar
              </div>
              <RContainer width="100%" height={300}>
                <RChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PGrid stroke="var(--card-border)" />
                  <PAngle dataKey="property" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <PRadius angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                  {molecules.map((_mol, i) => (
                    <RRadar
                      key={i}
                      name={names[i]}
                      dataKey={`c${i}`}
                      stroke={COMPOUND_COLORS[i]}
                      fill={COMPOUND_COLORS[i]}
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                  ))}
                  <RLegend
                    wrapperStyle={{ fontSize: 11, color: 'var(--text-muted)' }}
                  />
                </RChart>
              </RContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompoundComparison;
